import { Alert, Button, Group, Modal, Select, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { notify } from '@/lib/notify';
import { useMemo, useState } from 'react';
import { isUnifyPlatformStaffRole } from '@unify/fhir-model/staff-role-org';
import type { CreateUserPayload } from '../../../lib/adminDirectoryService';
import { formatUserProvisioningError } from '../../../lib/userProvisioningErrors';
import { getAssignableRolesForHeldRoles } from '../../../features/rbac';
import { usePermissions } from '../../../contexts/PermissionContext';
import {
  createUserAssignableRoleValues,
  createUserFormSchema,
  type CreateUserFormValues,
} from '../../../schemas/practitioner.schema';
import { type Practice, type User, ROLE_LABELS, type Role } from '../types/rbac';

function isCreateUserAssignableRole(value: string | null): value is CreateUserFormValues['role'] {
  return (
    value != null &&
    (createUserAssignableRoleValues as readonly string[]).includes(value)
  );
}

interface CreateUserModalProps {
  onClose: () => void;
  onCreateUser: (payload: CreateUserPayload) => Promise<User>;
  practices: Practice[];
  isPlatformView: boolean;
  currentPracticeId?: string;
  /** Singleton Unify platform org (type=other). Required to invite Unify Admin. */
  platformOrganization?: { id: string; name: string } | null;
  /** Default role selection when the modal opens. */
  defaultRole?: CreateUserFormValues['role'];
}

export function CreateUserModal({
  onClose,
  onCreateUser,
  practices,
  isPlatformView,
  currentPracticeId,
  platformOrganization = null,
  defaultRole = 'provider',
}: CreateUserModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { availableRoles, permissions } = usePermissions();
  const canInvite =
    permissions.edit_users === true
    && availableRoles.some((role) => isUnifyPlatformStaffRole(role));

  const roleOptions = useMemo(() => {
    const allowed = new Set(
      getAssignableRolesForHeldRoles(availableRoles, defaultRole as Role).filter((r) =>
        (createUserAssignableRoleValues as readonly string[]).includes(r),
      ),
    );
    return createUserAssignableRoleValues
      .filter((roleKey) => allowed.has(roleKey))
      .filter((roleKey) => isPlatformView || !isUnifyPlatformStaffRole(roleKey))
      .map((roleKey) => ({
        value: roleKey,
        label: ROLE_LABELS[roleKey],
      }));
  }, [availableRoles, defaultRole, isPlatformView]);

  const resolvedDefaultRole = useMemo((): CreateUserFormValues['role'] => {
    if (roleOptions.some((o) => o.value === defaultRole)) return defaultRole;
    return (roleOptions[0]?.value ?? 'provider') as CreateUserFormValues['role'];
  }, [roleOptions, defaultRole]);

  const validate = useMemo(() => zod4Resolver(createUserFormSchema), []);

  const initialPracticeId = currentPracticeId || practices[0]?.id || '';
  const initialOrgId = isUnifyPlatformStaffRole(resolvedDefaultRole)
    ? platformOrganization?.id || ''
    : initialPracticeId;

  const form = useForm<CreateUserFormValues>({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      role: resolvedDefaultRole,
      practiceId: initialOrgId,
    },
    validate,
  });

  const unifyRole = isUnifyPlatformStaffRole(form.values.role);
  const missingPlatformOrg = unifyRole && !platformOrganization?.id;

  const handleSubmit = form.onSubmit(async (values) => {
    if (!canInvite) {
      setSubmitError('You do not have permission to invite users.');
      return;
    }
    const orgId = isUnifyPlatformStaffRole(values.role)
      ? platformOrganization?.id || values.practiceId
      : values.practiceId;
    const selectedPractice = practices.find((p) => p.id === orgId);
    const payload: CreateUserPayload = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      role: values.role,
      practiceId: orgId,
      practiceName: isUnifyPlatformStaffRole(values.role)
        ? platformOrganization?.name || 'Unify'
        : selectedPractice?.name || '',
    };

    setSubmitting(true);
    setSubmitError(null);
    try {
      await onCreateUser(payload);
      const name = `${values.firstName} ${values.lastName}`;
      notify({
        title: 'User created',
        message: isUnifyPlatformStaffRole(values.role)
          ? `${name} has been added to Unify.`
          : `${name} has been added to the practice.`,
        type: 'success',
      });
      onClose();
    } catch (err) {
      setSubmitError(formatUserProvisioningError(err));
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <Modal
      opened={true}
      onClose={onClose}
      title={isPlatformView ? 'Create New User' : 'Add User to Practice'}
      size="lg"
      padding="lg"
      centered
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          {!canInvite && (
            <Alert color="red" title="Permission required">
              You need Manage users permission to invite staff.
            </Alert>
          )}
          <Select
            label="Primary role"
            description="Additional roles can be assigned later from Permissions."
            data={roleOptions}
            value={form.values.role}
            onChange={(value) => {
              const next = isCreateUserAssignableRole(value) ? value : resolvedDefaultRole;
              form.setFieldValue('role', next);
              if (isUnifyPlatformStaffRole(next)) {
                form.setFieldValue('practiceId', platformOrganization?.id || '');
              } else if (!form.values.practiceId || isUnifyPlatformStaffRole(form.values.role)) {
                form.setFieldValue('practiceId', initialPracticeId);
              }
            }}
            required
            disabled={!canInvite || roleOptions.length === 0}
            error={form.errors.role}
          />

          <Group grow>
            <TextInput
              label="Legal First Name"
              placeholder="e.g., Jane"
              {...form.getInputProps('firstName')}
              required
            />
            <TextInput
              label="Legal Last Name"
              placeholder="e.g., Doe"
              {...form.getInputProps('lastName')}
              required
            />
          </Group>
          <TextInput
            label="Email Address"
            type="email"
            placeholder="e.g., jane.doe@example.com"
            {...form.getInputProps('email')}
            required
          />
          {unifyRole ? (
            <TextInput
              label="Organization"
              value={platformOrganization?.name || ''}
              disabled
              required
              error={missingPlatformOrg ? 'Unify organization is not available.' : form.errors.practiceId}
            />
          ) : isPlatformView ? (
            <Select
              label="Practice"
              data={practices.map((p) => ({ value: p.id, label: p.name }))}
              value={form.values.practiceId}
              onChange={(value) => form.setFieldValue('practiceId', value || '')}
              required
              error={form.errors.practiceId}
            />
          ) : (
            <TextInput label="Practice" value={practices.find((p) => p.id === form.values.practiceId)?.name || ''} disabled />
          )}

          {missingPlatformOrg && (
            <Alert color="red" title="Unify organization required">
              Unify Admin must be tied to the Unify organization, which could not be loaded.
            </Alert>
          )}

          <Alert color="blue" title="Invitation Email">
            <Text size="xs">
              The user will receive an invitation email to set up their account and sign in.
            </Text>
          </Alert>

          {submitError && (
            <Alert color="red" title="Error">
              <Text size="xs">{submitError}</Text>
            </Alert>
          )}
        </Stack>
        <Group gap="sm" justify="flex-end" mt="xl">
          <Button variant="default" onClick={onClose} type="button" disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            loading={submitting}
            disabled={!canInvite || roleOptions.length === 0 || missingPlatformOrg}
          >
            Create User
          </Button>
        </Group>
      </form>
    </Modal>
  );
}
